/**
 * MedicationCapture - Komponente zum Fotografieren von Medikamentenlisten
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Check as CheckIcon
} from '@mui/icons-material';

const MedicationCapture = ({ onCapture }) => {
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Prüfe Dateityp
    if (!file.type.startsWith('image/')) {
      setError('Bitte wählen Sie ein Bild aus.');
      return;
    }

    // Prüfe Dateigröße (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Das Bild ist zu groß. Maximal 10MB erlaubt.');
      return;
    }

    setError(null);

    // Bild verkleinern und als Data URL speichern
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Canvas für Resize
        const canvas = document.createElement('canvas');
        const maxWidth = 1200;
        const maxHeight = 1600;
        let width = img.width;
        let height = img.height;

        // Resize wenn nötig
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Als JPEG mit 85% Qualität
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setPhoto(dataUrl);
        onCapture?.(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = () => {
    setPhoto(null);
    onCapture?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCapture = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: 'center',
        backgroundColor: '#fafafa'
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {!photo ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fotografieren Sie Ihre Medikamentenliste oder wählen Sie ein Bild aus.
          </Typography>

          <Button
            variant="contained"
            startIcon={<CameraIcon />}
            onClick={handleCapture}
            size="large"
          >
            Foto aufnehmen
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: '100%'
            }}
          >
            <img
              src={photo}
              alt="Medikamentenliste"
              style={{
                maxWidth: '100%',
                maxHeight: 300,
                borderRadius: 8,
                border: '2px solid #4caf50'
              }}
            />
            <IconButton
              onClick={handleDelete}
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                backgroundColor: '#f44336',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#d32f2f'
                }
              }}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CheckIcon color="success" />
            <Typography variant="body2" color="success.main">
              Foto gespeichert
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<CameraIcon />}
            onClick={handleCapture}
            size="small"
            sx={{ mt: 1 }}
          >
            Neues Foto
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default MedicationCapture;
